/**
 * THE FIELD TELLS THEM, IMMEDIATELY.
 *
 * A player must never discover that a word is unusable by LOSING A ROUND with
 * it. Before this, they typed it, submitted it, and silently scored 0 — which
 * reads as a bug, not a rule. Now the field lights up the moment the word is
 * complete, says why, and lets them change it with the clock still running.
 *
 * THIS IS A COURTESY, NOT THE RULE. The referee (`gameLogic.validateAnswer` →
 * `moderation.isProfaneWord`) refuses these words whether or not the browser
 * bothered to look. A modified client cannot smuggle anything past it; it just
 * gets the nastier version of the same answer, at the tally instead of at the
 * keyboard.
 *
 * ── WHY A GENERATED LIST AND NOT A SECOND SCREEN ────────────────────────────
 *
 * The obvious implementation is to run the profanity matcher in the browser
 * too. That is a SECOND IMPLEMENTATION of the rule, and two implementations of
 * one rule drift — this codebase has already paid for two copies of a
 * normalizer, twice. So the browser gets no opinions of its own: it gets
 * `blocked-words.json`, which is emitted by `backend/scripts/build-blocked-words.js`
 * from the server's own screen, run over the server's own four corpora. The
 * field and the referee cannot disagree about which words are refused, because
 * only one of them decides. A test rebuilds the list and diffs it against the
 * committed file, and a second test asserts this module agrees with
 * `moderation.isProfaneWord` on all 154,418 corpus words, one by one.
 *
 * ── WHY EXACT MATCH, NEVER SUBSTRING ────────────────────────────────────────
 *
 * `Fukuoka` is a Japanese city of 1.6 million. `Annalise` is a name. `Blue tit`
 * is a bird. `Scunthorpe` is a town. A substring screen flags every one of them
 * — 653 innocent corpus words in total, measured. Telling a real person that
 * their real answer is filth is not an annoyance, it is an accusation, and it
 * is a WORSE bug than the one this file fixes.
 *
 * So the answer must BE the word, not merely contain it. Set membership on the
 * whole folded answer gives us that for free, and makes the false positive
 * structurally impossible: "analuisa" is simply not the string "anal".
 */
import blockedWords from '../data/blocked-words.json';

/**
 * The sentence, from the referee's own verdict — not retyped here. See
 * `NOT_FOR_THIS_TABLE` in `backend/src/gameLogic.js`; the generator copies it
 * into the JSON so the field and the scoring screen say the same thing.
 */
export const NOT_FOR_THIS_TABLE: string = blockedWords.message;

/**
 * Fold, don't strip.
 *
 * `Anaël` is a real name — French and Hebrew, and it is sitting in names.json.
 * The server's first attempt DELETED the accent instead of folding it, which
 * turned "anaël" into "anal" and refused a nine-year-old's grandmother at her
 * own kitchen table. NFD splits `ë` into `e` + a combining mark; dropping the
 * mark leaves `e`, so the answer becomes "anael", which is a name, which is
 * what it is.
 */
const foldAccents = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '');

/** The answer as written: trimmed, folded, lower-cased, spaces collapsed. */
const fold = (answer: string): string =>
  foldAccents(String(answer ?? ''))
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');

/**
 * The answer with every separator removed, because "s l u t", "s-l-u-t" and
 * "slut" are the same word and all three of them know it. The server runs the
 * same second pass (`moderation.despace`).
 *
 * This cannot resurrect the Scunthorpe problem: it squashes the ANSWER, then
 * still demands an EXACT match. "Blue tit" squashes to "bluetit", which is not
 * a blocked word, so the bird is safe.
 */
const squash = (answer: string): string =>
  foldAccents(String(answer ?? '')).toLowerCase().replace(/[^a-z0-9]+/g, '');

/**
 * Both forms of every refused word.
 *
 * The list carries corpus spellings ("hand job"), but a player typing to beat a
 * 60-second clock writes "handjob" or "hand-job". The server catches those on
 * its despaced pass; indexing the squashed form here is how the field catches
 * them too. Adding the squashed form of an ALREADY-blocked word cannot create a
 * false positive — it can only recognise a word we already refuse.
 */
const REFUSED: ReadonlySet<string> = new Set(
  blockedWords.words.flatMap((w) => [fold(w), squash(w)]).filter(Boolean)
);

/**
 * Is this a word the referee will refuse?
 *
 * @param answer what the player has typed, prefix letter and all
 */
export function isBlockedAnswer(answer: string | null | undefined): boolean {
  if (!answer) return false;
  const written = fold(answer);
  if (!written) return false;
  if (REFUSED.has(written)) return true;
  return REFUSED.has(squash(written));
}

/**
 * The categories whose current answer the referee would refuse — in the order
 * the fields appear, so the caller can light them all and focus the first.
 */
export function blockedCategories(
  answers: Record<string, string>,
  categories: readonly string[]
): string[] {
  return categories.filter((c) => isBlockedAnswer(answers[c]));
}
